import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate } from "react-router-dom";

import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { OpenAppButton } from "../components/landing/OpenAppButton";
import { PhonePreview } from "../components/landing/PhonePreview";
import { APP_HOME_PATH, isStandaloneDisplay } from "../lib/app-paths";
import logo from "../match-response/logo.png";

type TitledCopy = {
  title: string;
  body: string;
};

type FaqItem = {
  q: string;
  a: string;
};

const Icon = ({ children }: { children: ReactNode }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {children}
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="pp-check">
    <path d="M5 12.5 9.2 17 19 7" />
  </svg>
);

const STEP_ICONS = [
  <Icon key="create">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
    <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3M12 13v5M9.5 15.5h5" />
  </Icon>,
  <Icon key="share">
    <circle cx="6" cy="12" r="2.2" />
    <circle cx="17" cy="6.5" r="2.2" />
    <circle cx="17" cy="17.5" r="2.2" />
    <path d="M8 11.1 14.8 7.6M8 12.9l6.8 3.5" />
  </Icon>,
  <Icon key="join">
    <circle cx="9" cy="8" r="2.6" />
    <path d="M3.8 19.2c.4-2.8 2.6-4.4 5.2-4.4s4.8 1.6 5.2 4.4" />
    <path d="M17 10.5v6M14 13.5h6" />
  </Icon>,
  <Icon key="done">
    <circle cx="12" cy="12" r="8" />
    <path d="M8.5 12.2 11 14.7 15.8 9.5" />
  </Icon>,
];

export const LandingPage = () => {
  const { t } = useTranslation();

  if (isStandaloneDisplay()) {
    return <Navigate to={APP_HOME_PATH} replace />;
  }

  const checks = t("landing.checks", { returnObjects: true }) as string[];
  const benefits = t("landing.benefits", {
    returnObjects: true,
  }) as TitledCopy[];
  const steps = t("landing.steps", { returnObjects: true }) as TitledCopy[];
  const highlightPoints = t("landing.highlightPoints", {
    returnObjects: true,
  }) as string[];
  const faqs = t("landing.faqs", { returnObjects: true }) as FaqItem[];

  return (
    <div className="pp-landing">
      <header className="pp-landing-header">
        <div className="pp-landing-header-inner">
          <a href="#inicio" className="pp-landing-brand">
            <img src={logo} alt="" width={36} height={36} />
            <span>pinpadel</span>
          </a>
          <div className="pp-landing-header-actions">
            <LanguageSwitcher />
            <OpenAppButton
              className="pp-open-app--shrink"
              label={t("landing.openAppShort")}
            />
          </div>
        </div>
      </header>

      <main id="inicio">
        <section className="pp-hero-wrap">
          <div className="pp-landing-wrap pp-hero">
            <div className="pp-hero-copy">
              <p className="pp-landing-badge">{t("landing.badge")}</p>
              <h1>{t("landing.title")}</h1>
              <p className="pp-hero-lead">{t("landing.subtitle")}</p>
              <div className="pp-hero-actions">
                <OpenAppButton
                  size="lg"
                  showArrow
                  className="pp-open-app--block"
                />
                <a
                  href="#como-funciona"
                  className="pp-open-app pp-open-app--lg pp-open-app--ghost"
                >
                  {t("landing.how")}
                </a>
              </div>
              <ul className="pp-checks">
                {checks.map((item) => (
                  <li key={item}>
                    <CheckIcon />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <PhonePreview />
          </div>
        </section>

        <section className="pp-benefits">
          <div className="pp-landing-wrap pp-benefits-grid">
            {benefits.map((item) => (
              <div key={item.title} className="pp-benefit">
                <h2>{item.title}</h2>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="como-funciona" className="pp-landing-wrap pp-section">
          <div className="pp-section-intro">
            <p className="pp-eyebrow">{t("landing.howEyebrow")}</p>
            <h2>{t("landing.howTitle")}</h2>
          </div>
          <ol className="pp-steps-grid">
            {steps.map((step, index) => (
              <li key={step.title}>
                <div className="pp-step-kicker">
                  <span className="pp-step-icon">{STEP_ICONS[index]}</span>
                  <span>{t("landing.stepLabel", { count: index + 1 })}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="pp-highlight">
          <div className="pp-landing-wrap pp-highlight-grid">
            <div>
              <p className="pp-landing-badge pp-landing-badge--invert">
                {t("landing.highlightEyebrow")}
              </p>
              <h2>{t("landing.highlightTitle")}</h2>
              <p>{t("landing.highlightBody")}</p>
            </div>
            <ul className="pp-highlight-list">
              {highlightPoints.map((item) => (
                <li key={item}>
                  <CheckIcon />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="faq" className="pp-landing-wrap pp-section">
          <div className="pp-section-intro">
            <p className="pp-eyebrow">{t("landing.faqEyebrow")}</p>
            <h2>{t("landing.faqTitle")}</h2>
          </div>
          <div className="pp-faq-list">
            {faqs.map((item) => (
              <details key={item.q} className="pp-faq">
                <summary>
                  <span>{item.q}</span>
                  <span className="pp-faq-plus" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="pp-closing">
          <div className="pp-landing-wrap pp-closing-inner">
            <div>
              <h2>{t("landing.ctaTitle")}</h2>
              <p>{t("landing.ctaBody")}</p>
            </div>
            <OpenAppButton size="lg" showArrow className="pp-open-app--block" />
          </div>
        </section>
      </main>

      <footer className="pp-landing-footer">
        <div className="pp-landing-wrap pp-landing-footer-inner">
          <div>
            <p>{t("landing.footer")}</p>
            <p>
              <Link to="/privacy">{t("common.privacy")}</Link>
            </p>
          </div>
          <p>{t("landing.footerNote")}</p>
        </div>
      </footer>

      <div className="pp-landing-bar">
        <OpenAppButton
          size="lg"
          showArrow
          label={t("landing.openAppShort")}
          className="pp-open-app--block"
        />
      </div>
    </div>
  );
};
